import { useStore } from '@/state/store';
import { FeedbackLink } from './FeedbackLink';
import { isNavigationDirective } from '@/music/ireal/parse';
import type { BarlineClose, BarlineOpen, SectionRole } from '@/music/ireal/types';
import { frac, fractionFromNumber, fractionToNumber, type Fraction } from '@/music/score/duration';
import type { BandCue, BandCueChannel } from '@/music/score/types';

interface MeasureEditorProps {
  measureId: string;
  compact?: boolean;
}

const OPEN_OPTIONS: { value: BarlineOpen | ''; label: string }[] = [
  { value: '', label: 'Single |' },
  { value: 'double', label: 'Double ||' },
  { value: 'repeat', label: 'Repeat start |:' },
];
const CLOSE_OPTIONS: { value: BarlineClose; label: string }[] = [
  { value: 'single', label: 'Single |' },
  { value: 'double', label: 'Double ||' },
  { value: 'repeat', label: 'Repeat end :|' },
  { value: 'final', label: 'Final ‖' },
];
const TIME_OPTIONS = ['inherit', '4/4', '3/4', '2/4', '6/8', '5/4', '12/8', '2/2'];
/** Common written feels. A free-text field, so this is a shortcut, not a set. */
const FEEL_SUGGESTIONS = ['Swing', 'Latin', 'Bossa', 'Samba', 'Even 8ths', 'Ballad', 'Funk'];
const BAND_CHANNEL_LABELS: Record<BandCueChannel, string> = {
  comp: 'Comping',
  bass: 'Bass',
  drums: 'Drums',
};
const NAVIGATION_OPTIONS = [
  '',
  'Fine',
  'To Coda',
  'D.C.',
  'D.C. al Fine',
  'D.C. al Coda',
  'D.S.',
  'D.S. al Fine',
  'D.S. al Coda',
] as const;

/** Per-measure structural editing: barlines, section, ending, time sig, markers. */
export function MeasureEditor({ measureId, compact = false }: MeasureEditorProps) {
  const chart = useStore((s) => s.chart);
  const measure = chart.measures.find((m) => m.id === measureId) ?? null;
  const index = chart.measures.findIndex((m) => m.id === measureId);
  const patchMeasure = useStore((s) => s.patchMeasure);
  const insertMeasureAfter = useStore((s) => s.insertMeasureAfter);
  const deleteMeasure = useStore((s) => s.deleteMeasure);

  if (!measure) return null;

  const defaultClose: BarlineClose = index === chart.measures.length - 1 ? 'final' : 'single';

  let activeMeter = chart.timeSignature;
  for (let i = 0; i <= index; i++) activeMeter = chart.measures[i]?.timeSig ?? activeMeter;
  const fullBarBeats = activeMeter[0];
  const barQuarters = ((measure.pickupBeats ?? fullBarBeats) * 4) / activeMeter[1];
  const bandCue = measure.bandCue;
  const cueChannels = bandCue?.channels;
  const cueScope =
    !cueChannels || new Set(cueChannels).size === 3
      ? 'all'
      : cueChannels.length === 1
        ? cueChannels[0]
        : 'custom';
  const hitLengths = Array.from({ length: Math.floor(barQuarters * 4) }, (_, optionIndex) =>
    frac(optionIndex + 1, 4),
  );
  for (const duration of [fractionFromNumber(barQuarters), bandCue?.duration]) {
    if (
      duration &&
      !hitLengths.some((candidate) => fractionToNumber(candidate) === fractionToNumber(duration))
    ) {
      hitLengths.push(duration);
    }
  }
  hitLengths.sort((a, b) => fractionToNumber(a) - fractionToNumber(b));
  const pickupOptions = Array.from(
    { length: Math.max(0, Math.ceil(fullBarBeats * 4) - 1) },
    (_, optionIndex) => (optionIndex + 1) / 4,
  );
  if (
    measure.pickupBeats !== undefined &&
    !pickupOptions.some((beats) => Math.abs(beats - measure.pickupBeats!) < 1e-9)
  ) {
    pickupOptions.push(measure.pickupBeats);
    pickupOptions.sort((a, b) => a - b);
  }

  return (
    <section className="analysis-panel measure-editor" data-testid="measure-editor">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Bar {index + 1}</h3>
        <div className="row">
          <button
            className="btn btn-sm"
            data-testid="insert-measure"
            onClick={() => insertMeasureAfter(measureId)}
          >
            Insert bar after
          </button>
          <button
            className="btn btn-sm btn-danger"
            data-testid="delete-measure"
            onClick={() => deleteMeasure(measureId)}
          >
            Delete bar
          </button>
        </div>
      </div>

      <div className="row measure-editor-quick-actions" role="group" aria-label="Quick form marks">
        <span className="muted">Quick marks</span>
        <button
          type="button"
          className="btn btn-sm"
          aria-label="Toggle repeat start"
          aria-pressed={measure.open === 'repeat'}
          onClick={() =>
            patchMeasure(measureId, {
              open: measure.open === 'repeat' ? undefined : 'repeat',
            })
          }
        >
          |: Repeat
        </button>
        <button
          type="button"
          className="btn btn-sm"
          aria-label="Toggle repeat end"
          aria-pressed={measure.close === 'repeat'}
          onClick={() =>
            patchMeasure(measureId, {
              close: measure.close === 'repeat' ? defaultClose : 'repeat',
            })
          }
        >
          Repeat :|
        </button>
        {[1, 2].map((ending) => (
          <button
            key={ending}
            type="button"
            className="btn btn-sm"
            aria-label={`Toggle ending ${ending}`}
            aria-pressed={measure.ending === ending}
            onClick={() =>
              patchMeasure(measureId, {
                ending: measure.ending === ending ? undefined : ending,
              })
            }
          >
            {ending}.
          </button>
        ))}
        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={!!measure.segno}
          onClick={() => patchMeasure(measureId, { segno: !measure.segno })}
        >
          Segno
        </button>
        <button
          type="button"
          className="btn btn-sm"
          aria-label="Toggle coda mark"
          aria-pressed={!!measure.coda}
          title="The first coda mark is To Coda; the final mark is the coda destination"
          onClick={() => patchMeasure(measureId, { coda: !measure.coda })}
        >
          Coda
        </button>
      </div>

      <details className="measure-editor-details" open={!compact}>
        <summary>More bar options</summary>
        <div className="control-bar" style={{ marginTop: 12 }}>
          <Field label="Section">
            <input
              className="text-input narrow"
              value={measure.section ?? ''}
              maxLength={6}
              placeholder="—"
              aria-label="Section label"
              data-testid="measure-section"
              onChange={(e) => patchMeasure(measureId, { section: e.target.value || undefined })}
            />
          </Field>
          <Field label="Role">
            <select
              value={measure.sectionRole ?? ''}
              aria-label="Section role"
              data-testid="measure-section-role"
              title="Performance role of the region starting here: an intro plays once at the top, a solos region is the written solo form, a coda is terminal"
              onChange={(e) =>
                patchMeasure(measureId, {
                  sectionRole: (e.target.value || undefined) as SectionRole | undefined,
                })
              }
            >
              <option value="">—</option>
              <option value="intro">Intro</option>
              <option value="solos">Solos</option>
              <option value="coda">Coda</option>
            </select>
          </Field>
          <Field label="Feel">
            {/* The authored Feel cue: a musical direction printed on the
                score, NOT a groove or drum-loop choice. Which groove plays a
                "Latin" is an arrangement decision made in the mixer. */}
            <input
              className="text-input narrow"
              list="feel-cue-suggestions"
              value={measure.feel?.label ?? ''}
              maxLength={16}
              placeholder="—"
              aria-label="Feel cue"
              data-testid="measure-feel"
              title="A written feel change taking effect at this bar (Latin, Swing, Bossa…). The band follows it; it is not a groove preset."
              onChange={(e) => {
                const label = e.target.value.trim();
                patchMeasure(measureId, {
                  // Preserve any authored within-measure offset while the
                  // label is edited; clearing the label removes the cue.
                  feel: label ? { ...measure.feel, label } : undefined,
                });
              }}
            />
          </Field>
          <datalist id="feel-cue-suggestions">
            {FEEL_SUGGESTIONS.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
          <Field label="Band cue">
            <select
              value={bandCue?.mode ?? ''}
              aria-label="Band cue"
              data-testid="measure-band-cue"
              title="An accompaniment instruction for this bar; the melody keeps playing"
              onChange={(event) => {
                const mode = event.target.value as BandCue['mode'] | '';
                patchMeasure(measureId, {
                  bandCue: mode ? { ...bandCue, mode } : undefined,
                });
              }}
            >
              <option value="">Normal</option>
              <option value="play">Play</option>
              <option value="rest">Rest</option>
              <option value="hit">Hit, then rest</option>
            </select>
          </Field>
          {bandCue && (
            <Field label="Applies to">
              <select
                value={cueScope}
                aria-label="Band cue instruments"
                data-testid="measure-band-cue-channels"
                onChange={(event) => {
                  if (event.target.value === 'custom') return;
                  const next = { ...bandCue };
                  if (event.target.value === 'all') delete next.channels;
                  else next.channels = [event.target.value as BandCueChannel];
                  patchMeasure(measureId, { bandCue: next });
                }}
              >
                <option value="all">Whole band</option>
                {Object.entries(BAND_CHANNEL_LABELS).map(([channel, label]) => (
                  <option key={channel} value={channel}>
                    {label}
                  </option>
                ))}
                {cueScope === 'custom' && (
                  <option value="custom">
                    {cueChannels!.map((channel) => BAND_CHANNEL_LABELS[channel]).join(' + ')}
                  </option>
                )}
              </select>
            </Field>
          )}
          {bandCue?.mode === 'hit' && (
            <Field label="Hit length (quarter notes)">
              <select
                value={bandCue.duration ? fractionKey(bandCue.duration) : 'default'}
                aria-label="Band hit length in quarter notes"
                data-testid="measure-band-cue-duration"
                onChange={(event) => {
                  const next = { ...bandCue };
                  if (event.target.value === 'default') delete next.duration;
                  else {
                    const duration = hitLengths.find(
                      (candidate) => fractionKey(candidate) === event.target.value,
                    );
                    if (!duration || fractionToNumber(duration) > barQuarters) return;
                    next.duration = duration;
                  }
                  patchMeasure(measureId, { bandCue: next });
                }}
              >
                <option value="default">{Math.min(1, barQuarters)} (default)</option>
                {hitLengths.map((duration) => (
                  <option
                    key={fractionKey(duration)}
                    value={fractionKey(duration)}
                    disabled={fractionToNumber(duration) > barQuarters}
                  >
                    {formatQuarterLength(duration)}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Open">
            <select
              value={measure.open ?? ''}
              aria-label="Open barline"
              onChange={(e) =>
                patchMeasure(measureId, {
                  open: (e.target.value || undefined) as BarlineOpen | undefined,
                })
              }
            >
              {OPEN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Close">
            <select
              value={measure.close ?? 'single'}
              aria-label="Close barline"
              data-testid="measure-close"
              onChange={(e) => patchMeasure(measureId, { close: e.target.value as BarlineClose })}
            >
              {CLOSE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Ending">
            <select
              value={measure.ending ?? ''}
              aria-label="Ending"
              data-testid="measure-ending"
              onChange={(e) =>
                patchMeasure(measureId, {
                  ending: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            >
              <option value="">none</option>
              {[1, 2, 3].map((n) => (
                <option key={n} value={n}>
                  {n}.
                </option>
              ))}
            </select>
          </Field>
          <Field label="Time">
            <select
              value={measure.timeSig ? `${measure.timeSig[0]}/${measure.timeSig[1]}` : 'inherit'}
              aria-label="Measure time signature"
              onChange={(e) => {
                if (e.target.value === 'inherit') patchMeasure(measureId, { timeSig: undefined });
                else {
                  const [n, d] = e.target.value.split('/').map(Number);
                  patchMeasure(measureId, { timeSig: [n, d] });
                }
              }}
            >
              {TIME_OPTIONS.map((ts) => (
                <option key={ts} value={ts}>
                  {ts}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Bar length">
            <select
              value={measure.pickupBeats ?? 'full'}
              aria-label="Measure bar length"
              data-testid="measure-pickup-beats"
              title="Use a shorter beat count for an anacrusis (pickup); Full bar removes it"
              onChange={(e) => {
                const pickupBeats = e.target.value === 'full' ? undefined : Number(e.target.value);
                if (!patchMeasure(measureId, { pickupBeats })) {
                  window.alert('That pickup is too short for this bar’s existing chords or notes.');
                }
              }}
            >
              <option value="full">Full bar ({formatBeatCount(fullBarBeats)})</option>
              {pickupOptions.map((beats) => (
                <option key={beats} value={beats}>
                  Pickup: {formatBeatCount(beats)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Bar repeat">
            <select
              value={measure.barRepeat ?? ''}
              aria-label="Bar repeat"
              onChange={(e) =>
                patchMeasure(measureId, {
                  barRepeat: e.target.value ? (Number(e.target.value) as 1 | 2) : undefined,
                })
              }
            >
              <option value="">none</option>
              <option value={1}>1 bar (%)</option>
              <option value={2}>2 bars</option>
            </select>
          </Field>
        </div>

        <div className="row" style={{ gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!measure.segno}
              onChange={(e) => patchMeasure(measureId, { segno: e.target.checked })}
            />
            Segno
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!measure.coda}
              onChange={(e) => patchMeasure(measureId, { coda: e.target.checked })}
            />
            Coda
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!measure.fermata}
              onChange={(e) => patchMeasure(measureId, { fermata: e.target.checked })}
            />
            Fermata
          </label>
          <Field label="Navigation">
            <select
              value={measure.directive ?? ''}
              aria-label="Navigation directive"
              onChange={(e) =>
                patchMeasure(measureId, {
                  directive: e.target.value || undefined,
                  ...(e.target.value ? { staffText: undefined } : {}),
                })
              }
            >
              {measure.directive &&
                !NAVIGATION_OPTIONS.some((candidate) => candidate === measure.directive) && (
                  <option value={measure.directive}>{measure.directive}</option>
                )}
              {NAVIGATION_OPTIONS.map((directive) => (
                <option key={directive || 'none'} value={directive}>
                  {directive || 'none'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Text / directive">
            <input
              className="text-input"
              value={measure.directive ?? measure.staffText ?? ''}
              placeholder="e.g. D.C. al Coda"
              aria-label="Staff text"
              onChange={(e) => {
                const v = e.target.value || undefined;
                // Route recognised navigation phrases to `directive` so playback
                // (flatten) honours them; everything else is plain staff text.
                if (v && isNavigationDirective(v))
                  patchMeasure(measureId, { directive: v, staffText: undefined });
                else patchMeasure(measureId, { staffText: v, directive: undefined });
              }}
            />
          </Field>
        </div>
      </details>
      <FeedbackLink kind="tune" bar={index + 1}>Report a problem with this bar</FeedbackLink>
    </section>
  );
}

function formatBeatCount(beats: number): string {
  return `${beats} beat${beats === 1 ? '' : 's'}`;
}

function fractionKey({ num, den }: Fraction): string {
  return `${num}/${den}`;
}

function formatQuarterLength(duration: Fraction): string {
  const value = fractionToNumber(duration);
  return Number.isInteger(value * 4) ? String(value) : fractionKey(duration);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="control-group">
      <span className="label">{label}</span>
      {children}
    </div>
  );
}
